import { useEffect, useState, useRef } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Bell, Award, Users, Skull, Moon, Gift, ScrollText } from "lucide-react";

const KIND_ICON = {
  title: Award,
  guild: Users,
  worldboss: Skull,
  event: Moon,
  referral: Gift,
  recruit: ScrollText,
};
const KIND_COLOR = {
  title: "#B89C3A",
  guild: "#4A7BC7",
  worldboss: "#8A0303",
  event: "#E33333",
  referral: "#E5C34A",
  recruit: "#6E8B3D",
};

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState({ notifications: [], unread: 0 });
  const seenIds = useRef(new Set());

  const refresh = async () => {
    try {
      const d = await api.notifications();
      // Toast for new unread notifs since last poll
      d.notifications.forEach(n => {
        if (!n.read && !seenIds.current.has(n.notif_id)) {
          seenIds.current.add(n.notif_id);
          if (seenIds.current.size > 1) { // skip initial load
            toast(n.message, { description: n.kind });
          }
        }
      });
      // Initial: mark all as seen without toasting
      if (seenIds.current.size <= 1 && d.notifications.length > 0) {
        d.notifications.forEach(n => seenIds.current.add(n.notif_id));
      }
      setData(d);
    } catch (e) { /* silent */ }
  };

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 30000);
    return () => clearInterval(t);
  }, []);

  const markAllRead = async () => {
    try { await api.notificationsRead(); await refresh(); }
    catch (e) { /* silent */ }
  };

  return (
    <div className="relative" data-testid="notification-bell-wrap">
      <button
        data-testid="notification-bell"
        onClick={() => { setOpen(!open); if (!open) markAllRead(); }}
        className="relative text-[#8c8c8c] hover:text-[#E55A00] shrink-0"
        aria-label="Notifications"
      >
        <Bell className="w-4 h-4" strokeWidth={1.5} />
        {data.unread > 0 && (
          <span
            data-testid="notification-badge"
            className="absolute -top-1 -right-1 w-3 h-3 bg-[#8A0303] text-[8px] font-mono text-white flex items-center justify-center"
          >
            {data.unread > 9 ? "9+" : data.unread}
          </span>
        )}
      </button>

      {open && (
        <div
          data-testid="notification-panel"
          className="absolute right-0 top-6 w-72 max-h-96 overflow-y-auto panel bg-[#050505] z-50"
        >
          <div className="px-3 py-2 border-b border-[#262626] flex items-center justify-between">
            <span className="font-heading text-xs tracking-[0.3em] uppercase text-[#8c8c8c]">
              Notifications
            </span>
            <button onClick={() => setOpen(false)} className="text-[#666] hover:text-[#E55A00] text-xs">✕</button>
          </div>
          {data.notifications.length === 0 && (
            <p className="italic text-[#666] text-xs px-3 py-4">The messenger has nothing to deliver.</p>
          )}
          {data.notifications.map(n => {
            const Icon = KIND_ICON[n.kind] || Bell;
            const color = KIND_COLOR[n.kind] || "#8c8c8c";
            return (
              <div
                key={n.notif_id}
                data-testid={`notif-${n.notif_id}`}
                className="p-2 border-b border-[#1a1a1a] flex gap-2"
              >
                <Icon className="w-3 h-3 mt-0.5 shrink-0" style={{ color }} strokeWidth={1.5} />
                <div className="min-w-0 flex-1">
                  <div className="text-xs text-[#E2DFD8]">{n.message}</div>
                  <div className="font-mono text-[9px] text-[#666] uppercase tracking-widest mt-0.5">
                    {n.kind}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
